import { v4 as uuidv4 } from 'uuid';
import { notifyStatusChange, notifyApprovalNeeded } from './notificationService';

// Mock database using localStorage
const STORAGE_KEY = 'acom_requests';

// Request status types
export const RequestStatus = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  COMPLETED: 'completed',
  HOLD: 'hold',
};

// Initialize with some mock data if empty
const initializeData = () => {
  const existingData = localStorage.getItem(STORAGE_KEY);
  console.log('[initializeData] Existing data:', existingData ? 'Found' : 'Not found');
  if (!existingData) {
    console.log('[initializeData] Initializing with mock data...');
    const mockData = [
      {
        id: uuidv4(),
        type: 'leave',
        title: 'Annual Leave - December 2025',
        submittedDate: new Date('2025-07-15').toISOString(),
        status: RequestStatus.APPROVED,
        submittedBy: 'current.user@acom.edu.au',
        description: 'Annual leave for Christmas holidays',
        details: {
          startDate: '2025-12-20',
          endDate: '2025-12-31',
          reason: 'Family vacation',
        },
      },
      {
        id: uuidv4(),
        type: 'travel',
        title: 'Conference Travel - Sydney',
        submittedDate: new Date('2025-07-20').toISOString(),
        status: RequestStatus.PENDING,
        submittedBy: 'current.user@acom.edu.au',
        description: 'Travel request for education conference',
        details: {
          destination: 'Sydney',
          departureDate: '2025-08-15',
          returnDate: '2025-08-18',
          purpose: 'Education Technology Conference',
        },
      },
      {
        id: uuidv4(),
        type: 'reimbursement',
        title: 'Office Supplies Reimbursement',
        submittedDate: new Date('2025-07-18').toISOString(),
        status: RequestStatus.IN_PROGRESS,
        submittedBy: 'current.user@acom.edu.au',
        description: 'Reimbursement for office supplies',
        details: {
          amount: 245.50,
          currency: 'AUD',
          category: 'Office Supplies',
          receipts: 'attached',
        },
      },
    ];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(mockData));
  }
};

// Get all requests for current user
export const getMyRequests = (userEmail) => {
  // Don't reinitialize if data exists
  const existingData = localStorage.getItem(STORAGE_KEY);
  if (!existingData) {
    initializeData();
  }
  
  const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  return data.filter(request => request.submittedBy === userEmail || request.submittedBy === 'current.user@acom.edu.au');
};

// Get request by ID
export const getRequestById = (id) => {
  const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  console.log(`[getRequestById] Looking for ${id} in ${data.length} requests`);
  const found = data.find(request => request.id === id);
  console.log(`[getRequestById] Found:`, found ? 'YES' : 'NO');
  return found;
};

// Create new request
export const createRequest = (requestData) => {
  console.log('[createRequest] Creating new request:', requestData);
  const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  console.log('[createRequest] Current data length:', data.length);
  
  const newRequest = {
    id: uuidv4(),
    ...requestData,
    submittedDate: new Date().toISOString(),
    status: RequestStatus.PENDING,
  };
  console.log('[createRequest] New request:', newRequest);
  
  data.push(newRequest);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  console.log('[createRequest] Data saved. New length:', data.length);
  
  // Notify approver(s) if request needs approval
  if (newRequest.status === RequestStatus.PENDING) {
    // Single approver (extensions)
    if (newRequest.approver) {
      notifyApprovalNeeded(newRequest, newRequest.approver);
    }
    
    // Multiple approvers (academic changes)
    if (newRequest.approvers && Array.isArray(newRequest.approvers)) {
      newRequest.approvers.forEach(approverEmail => {
        notifyApprovalNeeded(newRequest, approverEmail);
      });
    }
  }
  
  return newRequest;
};

// Update request status
export const updateRequestStatus = (id, status, updatedByEmail) => {
  const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  const index = data.findIndex(request => request.id === id);
  if (index !== -1) {
    const oldStatus = data[index].status;
    data[index].status = status;
    data[index].lastUpdated = new Date().toISOString();
    data[index].updatedBy = updatedByEmail;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    
    // Create notification for status change
    if (oldStatus !== status && updatedByEmail) {
      notifyStatusChange(data[index], oldStatus, status, updatedByEmail);
    }
    
    return data[index];
  }
  return null;
};

// Delete request
export const deleteRequest = (id) => {
  const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  const filtered = data.filter(request => request.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  return true;
};

// Get all requests (for approvers)
export const getAllRequests = () => {
  // Don't reinitialize if data exists
  const existingData = localStorage.getItem(STORAGE_KEY);
  if (!existingData) {
    initializeData();
  }
  
  const requests = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  console.log('[getAllRequests] Retrieved', requests.length, 'requests from localStorage');
  console.log('[getAllRequests] Raw data:', requests);
  
  // Log academic requests specifically
  const academicRequests = requests.filter(r => r.type === 'academic');
  console.log('[getAllRequests] Academic requests found:', academicRequests.length);
  academicRequests.forEach(req => {
    console.log(`[getAllRequests] Academic: ${req.id} - ${req.title} - Status: ${req.status} - Approvers: ${JSON.stringify(req.approvers)}`);
  });
  
  // Sort by submittedDate (newer) or createdAt (older data)
  return requests.sort((a, b) => {
    const dateA = new Date(a.submittedDate || a.createdAt || 0);
    const dateB = new Date(b.submittedDate || b.createdAt || 0);
    return dateB - dateA;
  });
};

// Get statistics for dashboard
export const getRequestStats = (userEmail) => {
  const myRequests = getMyRequests(userEmail);
  return {
    pending: myRequests.filter(r => r.status === RequestStatus.PENDING).length,
    inProgress: myRequests.filter(r => r.status === RequestStatus.IN_PROGRESS || r.status === RequestStatus.HOLD).length,
    approved: myRequests.filter(r => r.status === RequestStatus.APPROVED).length,
    rejected: myRequests.filter(r => r.status === RequestStatus.REJECTED).length,
    completed: myRequests.filter(r => r.status === RequestStatus.COMPLETED).length,
    total: myRequests.length,
  };
};

// Add comment to request
export const addCommentToRequest = (requestId, comment) => {
  const allRequests = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  const index = allRequests.findIndex(r => r.id === requestId);
  
  if (index !== -1) {
    allRequests[index].notes = allRequests[index].notes || [];
    allRequests[index].notes.push(comment);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allRequests));
    return allRequests[index];
  }
  
  return null;
};